import React, { useState, useMemo, useCallback } from 'react';
import { Search, UserPlus, Check, X, ChevronDown, Trash2, Plus, Edit3 } from 'lucide-react';
import { sans, mono, green, greenLight, greenDark, useTheme } from '../../tokens';
import { useCompany } from '../../CompanyContext';

const DEFAULT_ROLES = [
  { name: 'Admin', key: 'admin', editable: false, permissions: { dashboard: 'edit', close: 'edit', fpa: 'edit', equity: 'edit', investor: 'edit', settings: 'edit' } },
  { name: 'Controller', key: 'controller', editable: true, permissions: { dashboard: 'edit', close: 'edit', fpa: 'edit', equity: 'edit', investor: 'view', settings: 'view' } },
  { name: 'Team Member', key: 'team_member', editable: true, permissions: { dashboard: 'view', close: 'edit', fpa: 'view', equity: 'none', investor: 'none', settings: 'none' } },
  { name: 'Investor', key: 'investor', editable: true, permissions: { dashboard: 'none', close: 'none', fpa: 'none', equity: 'none', investor: 'view', settings: 'none' } },
  { name: 'Auditor', key: 'auditor', editable: true, permissions: { dashboard: 'view', close: 'view', fpa: 'view', equity: 'view', investor: 'view', settings: 'none' } },
  { name: 'Board Member', key: 'board', editable: true, permissions: { dashboard: 'none', close: 'none', fpa: 'none', equity: 'none', investor: 'view', settings: 'none' } },
];

const MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'close', label: 'Close' },
  { key: 'fpa', label: 'FP&A' },
  { key: 'equity', label: 'Equity' },
  { key: 'investor', label: 'Investor' },
  { key: 'settings', label: 'Settings' },
];

const PERM_CYCLE = ['none', 'view', 'edit'];

const SAMPLE_USERS = [
  { id: 1, firstName: 'Taylor', lastName: 'Brooks', email: 'tbrooks@example.com', role: 'admin', status: 'active', lastLogin: '2026-03-30', twoFa: true },
  { id: 2, firstName: 'Jordan', lastName: 'Lee', email: 'jlee@example.com', role: 'controller', status: 'active', lastLogin: '2026-03-29', twoFa: true },
  { id: 3, firstName: 'Sam', lastName: 'Rivera', email: 'srivera@example.com', role: 'team_member', status: 'active', lastLogin: '2026-03-28', twoFa: false },
  { id: 4, firstName: 'Casey', lastName: 'Nguyen', email: 'cnguyen@example.com', role: 'controller', status: 'active', lastLogin: '2026-03-27', twoFa: true },
  { id: 5, firstName: 'External', lastName: 'Auditor', email: 'auditor@demoaudit.example.com', role: 'auditor', status: 'invited', lastLogin: null, twoFa: false },
];

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

function loadRoles() {
  const saved = localStorage.getItem('finkloz_custom_roles');
  if (saved) {
    try { return JSON.parse(saved); } catch { /* fall through */ }
  }
  return DEFAULT_ROLES;
}

function saveRoles(roles) {
  localStorage.setItem('finkloz_custom_roles', JSON.stringify(roles));
}

function PermCell({ value, onClick, disabled }) {
  const t = useTheme();
  const styles = {
    none: { bg: 'transparent', color: t.textFaint, label: '--' },
    view: { bg: '#E3F2FD', color: '#1565C0', label: 'View' },
    edit: { bg: greenLight, color: green, label: 'Edit' },
  };
  const s = styles[value] || styles.none;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Admin permissions cannot be changed' : 'Click to cycle: none / view / edit'}
      style={{
        padding: '3px 10px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        background: s.bg,
        color: s.color,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: sans,
        minWidth: 48,
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {s.label}
    </button>
  );
}

export default function UsersRolesSettings() {
  const t = useTheme();
  const { activeProfile } = useCompany();
  const savedTeam = JSON.parse(localStorage.getItem('finkloz_team_members') || 'null');
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', role: 'team_member' });
  const [users, setUsers] = useState(savedTeam?.members || SAMPLE_USERS);
  const [showMatrix, setShowMatrix] = useState(false);
  const [roles, setRoles] = useState(loadRoles);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRoleKey, setEditingRoleKey] = useState(null);
  const [editingRoleName, setEditingRoleName] = useState('');

  const persistRoles = useCallback((updated) => {
    setRoles(updated);
    saveRoles(updated);
  }, []);

  const persist = (updatedUsers) => {
    setUsers(updatedUsers);
    localStorage.setItem('finkloz_team_members', JSON.stringify({ members: updatedUsers }));
  };

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleInvite = () => {
    if (!inviteForm.email || !inviteForm.firstName || !inviteForm.lastName) return;
    const newUser = {
      id: Date.now(),
      ...inviteForm,
      status: 'invited',
      lastLogin: null,
      twoFa: false,
    };
    persist([...users, newUser]);
    setInviteForm({ email: '', firstName: '', lastName: '', role: 'team_member' });
    setShowInvite(false);
  };

  const handleRoleChange = (userId, newRole) => {
    persist(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleToggleStatus = (userId) => {
    persist(users.map(u => {
      if (u.id !== userId) return u;
      const newStatus = u.status === 'active' ? 'deactivated' : 'active';
      return { ...u, status: newStatus };
    }));
  };

  const handleDelete = (user) => {
    if (user.status === 'active') return;
    if (!window.confirm(`Remove ${user.firstName} ${user.lastName} (${user.email})?`)) return;
    persist(users.filter(u => u.id !== user.id));
  };

  // Role management
  const handlePermClick = (roleKey, modKey) => {
    const role = roles.find(r => r.key === roleKey);
    if (!role || !role.editable) return;
    const current = role.permissions[modKey] || 'none';
    const idx = PERM_CYCLE.indexOf(current);
    const next = PERM_CYCLE[(idx + 1) % PERM_CYCLE.length];
    const updated = roles.map(r =>
      r.key === roleKey
        ? { ...r, permissions: { ...r.permissions, [modKey]: next } }
        : r
    );
    persistRoles(updated);
  };

  const handleCreateRole = () => {
    const name = newRoleName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (roles.some(r => r.key === key)) {
      alert('A role with this key already exists.');
      return;
    }
    const newRole = {
      name,
      key,
      editable: true,
      permissions: { dashboard: 'none', close: 'none', fpa: 'none', equity: 'none', investor: 'none', settings: 'none' },
    };
    persistRoles([...roles, newRole]);
    setNewRoleName('');
    setShowCreateRole(false);
  };

  const handleDeleteRole = (roleKey) => {
    const role = roles.find(r => r.key === roleKey);
    if (!role || !role.editable) return;
    if (!window.confirm(`Delete role "${role.name}"? Users with this role will need to be reassigned.`)) return;
    persistRoles(roles.filter(r => r.key !== roleKey));
  };

  const handleStartRename = (role) => {
    if (!role.editable) return;
    setEditingRoleKey(role.key);
    setEditingRoleName(role.name);
  };

  const handleFinishRename = () => {
    const name = editingRoleName.trim();
    if (name && editingRoleKey) {
      persistRoles(roles.map(r =>
        r.key === editingRoleKey ? { ...r, name } : r
      ));
    }
    setEditingRoleKey(null);
    setEditingRoleName('');
  };

  const getRoleName = (key) => {
    const r = roles.find(r => r.key === key);
    return r ? r.name : key;
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Users & Roles
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Manage team members and their access levels.
      </p>

      {/* Search + Invite */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle(t), paddingLeft: 32 }}
          />
        </div>
        <button
          onClick={() => setShowInvite(!showInvite)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px',
            border: 'none',
            borderRadius: 6,
            background: green,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: sans,
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
        >
          <UserPlus size={14} />
          Invite User
        </button>
      </div>

      {/* Invite Form */}
      {showInvite && (
        <div style={{
          background: t.bgSurface,
          border: '1px solid ' + t.border,
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans, marginBottom: 12 }}>
            Invite New User
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle(t)}>Email *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                style={inputStyle(t)}
                placeholder="user@company.com"
              />
            </div>
            <div>
              <label style={labelStyle(t)}>First Name *</label>
              <input
                type="text"
                value={inviteForm.firstName}
                onChange={e => setInviteForm(p => ({ ...p, firstName: e.target.value }))}
                style={inputStyle(t)}
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Last Name *</label>
              <input
                type="text"
                value={inviteForm.lastName}
                onChange={e => setInviteForm(p => ({ ...p, lastName: e.target.value }))}
                style={inputStyle(t)}
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Role</label>
              <select
                value={inviteForm.role}
                onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))}
                style={inputStyle(t)}
              >
                {roles.map(r => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleInvite} style={{
                padding: '8px 14px', border: 'none', borderRadius: 6,
                background: green, color: '#fff', fontSize: 12, fontWeight: 600,
                fontFamily: sans, cursor: 'pointer',
              }}>
                Send
              </button>
              <button onClick={() => setShowInvite(false)} style={{
                padding: '8px 10px', border: '1px solid ' + t.border, borderRadius: 6,
                background: 'transparent', color: t.textSecondary, fontSize: 12,
                fontFamily: sans, cursor: 'pointer',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>
          {/* Role description */}
          {inviteForm.role && (
            <div style={{ marginTop: 8, fontSize: 11, color: t.textMuted, fontFamily: sans }}>
              {getRoleName(inviteForm.role)}
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Name', 'Email', 'Role', 'Status', 'Last Login', '2FA', ''].map((h, i) => (
                <th key={h || 'actions-' + i} style={{
                  padding: '10px 14px',
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                  ...(h === '' ? { width: 80 } : {}),
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => {
              const canDelete = u.status === 'invited' || u.status === 'deactivated';
              return (
                <tr key={u.id} style={{
                  borderBottom: '1px solid ' + t.borderLight,
                  opacity: u.status === 'deactivated' ? 0.55 : 1,
                }}>
                  <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                    {u.firstName} {u.lastName}
                  </td>
                  <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                    {u.email}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      style={{
                        fontFamily: sans,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 6px',
                        borderRadius: 4,
                        border: '1px solid ' + t.border,
                        background: u.role === 'admin' ? greenLight : t.pillBg,
                        color: u.role === 'admin' ? green : t.textSecondary,
                        cursor: 'pointer',
                        outline: 'none',
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                      }}
                    >
                      {roles.map(r => (
                        <option key={r.key} value={r.key}>{r.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: u.status === 'active' ? greenLight
                        : u.status === 'deactivated' ? '#FFEBEE'
                        : '#FFF3E0',
                      color: u.status === 'active' ? green
                        : u.status === 'deactivated' ? '#C62828'
                        : '#BA7517',
                    }}>
                      {u.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', color: t.textMuted, fontSize: 11, fontFamily: mono }}>
                    {u.lastLogin || '--'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {u.twoFa ? (
                      <Check size={14} style={{ color: green }} />
                    ) : (
                      <span style={{ color: t.textFaint, fontSize: 11 }}>--</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleStatus(u.id)}
                        title={u.status === 'active' ? 'Deactivate user' : 'Reactivate user'}
                        style={{
                          padding: '3px 8px',
                          border: '1px solid ' + t.border,
                          borderRadius: 4,
                          background: 'transparent',
                          color: u.status === 'active' ? '#BA7517' : green,
                          fontSize: 10,
                          fontWeight: 600,
                          fontFamily: sans,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {u.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(u)}
                          title="Delete user"
                          style={{
                            padding: '3px 5px',
                            border: '1px solid ' + t.border,
                            borderRadius: 4,
                            background: 'transparent',
                            color: '#C62828',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Role Permission Matrix */}
      <div>
        <button
          onClick={() => setShowMatrix(!showMatrix)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: sans,
            padding: 0, marginBottom: 12,
          }}
        >
          Role Permission Matrix
          <ChevronDown size={14} style={{
            transition: 'transform 0.2s',
            transform: showMatrix ? 'rotate(180deg)' : 'rotate(0deg)',
            color: t.textMuted,
          }} />
        </button>

        {showMatrix && (
          <div>
            {/* Create Role button */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
              {!showCreateRole ? (
                <button
                  onClick={() => setShowCreateRole(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px',
                    border: '1px solid ' + t.border,
                    borderRadius: 6,
                    background: 'transparent',
                    color: t.textPrimary,
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: sans,
                    cursor: 'pointer',
                  }}
                >
                  <Plus size={12} />
                  Create Role
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Role name..."
                    value={newRoleName}
                    onChange={e => setNewRoleName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateRole(); if (e.key === 'Escape') { setShowCreateRole(false); setNewRoleName(''); } }}
                    autoFocus
                    style={{ ...inputStyle(t), width: 180 }}
                  />
                  <button onClick={handleCreateRole} style={{
                    padding: '6px 12px', border: 'none', borderRadius: 6,
                    background: green, color: '#fff', fontSize: 11, fontWeight: 600,
                    fontFamily: sans, cursor: 'pointer',
                  }}>
                    Add
                  </button>
                  <button onClick={() => { setShowCreateRole(false); setNewRoleName(''); }} style={{
                    padding: '6px 8px', border: '1px solid ' + t.border, borderRadius: 6,
                    background: 'transparent', color: t.textSecondary, fontSize: 11,
                    fontFamily: sans, cursor: 'pointer',
                  }}>
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>

            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginBottom: 12 }}>
              Click a cell to cycle permission: -- / View / Edit. Click a role name to rename it.
            </div>

            {/* Full matrix table */}
            <div style={{
              border: '1px solid ' + t.border,
              borderRadius: 8,
              overflow: 'auto',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12, minWidth: roles.length * 80 + 120 }}>
                <thead>
                  <tr style={{ background: t.bgSurface }}>
                    <th style={{
                      padding: '10px 14px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.5, color: t.textMuted,
                      borderBottom: '1px solid ' + t.border,
                      position: 'sticky', left: 0, background: t.bgSurface, zIndex: 1,
                    }}>
                      Module
                    </th>
                    {roles.map(role => (
                      <th key={role.key} style={{
                        padding: '8px 10px', textAlign: 'center',
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.5, color: t.textMuted,
                        borderBottom: '1px solid ' + t.border,
                        minWidth: 72,
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          {editingRoleKey === role.key ? (
                            <input
                              type="text"
                              value={editingRoleName}
                              onChange={e => setEditingRoleName(e.target.value)}
                              onBlur={handleFinishRename}
                              onKeyDown={e => { if (e.key === 'Enter') handleFinishRename(); if (e.key === 'Escape') { setEditingRoleKey(null); setEditingRoleName(''); } }}
                              autoFocus
                              style={{
                                fontFamily: sans, fontSize: 10, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.5,
                                border: '1px solid ' + green, borderRadius: 3,
                                padding: '2px 4px', textAlign: 'center',
                                background: t.inputBg, color: t.textPrimary,
                                outline: 'none', width: 70,
                              }}
                            />
                          ) : (
                            <span
                              onClick={() => handleStartRename(role)}
                              style={{ cursor: role.editable ? 'pointer' : 'default' }}
                              title={role.editable ? 'Click to rename' : ''}
                            >
                              {role.name}
                            </span>
                          )}
                          {role.editable && (
                            <button
                              onClick={() => handleDeleteRole(role.key)}
                              title={`Delete ${role.name}`}
                              style={{
                                padding: 0, border: 'none', background: 'transparent',
                                color: '#C62828', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', opacity: 0.6,
                              }}
                            >
                              <Trash2 size={10} />
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(mod => (
                    <tr key={mod.key} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                      <td style={{
                        padding: '10px 14px', color: t.textPrimary, fontWeight: 500,
                        position: 'sticky', left: 0, background: t.bg, zIndex: 1,
                      }}>
                        {mod.label}
                      </td>
                      {roles.map(role => (
                        <td key={role.key} style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <PermCell
                            value={role.permissions[mod.key] || 'none'}
                            onClick={() => handlePermClick(role.key, mod.key)}
                            disabled={!role.editable}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
